import type { CrossBrandPromptItem } from "./types";
import { ensureCrossBrandBootstrap } from "./store/bootstrapStore";
import { shouldTreatHidsAsCrossBrand } from "./utils/hidPickerScope";
import { isCrossBrandPromptSuppressedForDesktopSession } from "./utils/desktopSessionPrompt";
import { resolveCrossBrandPromptItems } from "./utils/resolvePromptItems";

let promptHandler: ((items: CrossBrandPromptItem[]) => void) | null = null;
let scanScheduled = false;
let startupScanCoalesceTimer: ReturnType<typeof setTimeout> | null = null;

export function registerCrossBrandPromptHandler(
  handler: ((items: CrossBrandPromptItem[]) => void) | null
): void {
  promptHandler = handler;
}

async function readAuthorizedHidDevices(): Promise<
  { vendorId: number; productId: number; productName?: string }[]
> {
  if (typeof navigator === "undefined" || !navigator.hid?.getDevices) {
    return [];
  }
  try {
    const devices = await navigator.hid.getDevices();
    return devices.map((d) => ({
      vendorId: d.vendorId,
      productId: d.productId,
      productName: d.productName,
    }));
  } catch {
    return [];
  }
}

export type CrossBrandScanTrigger = "startup" | "hotplug";

/**
 * 扫描已授权 HID 中的异品牌设备并弹窗。
 * - startup：进应用 / rescan 后（桌面端依赖此项）
 * - hotplug：仅当后台开启 scanOnHotplugEnabled 时执行
 */
async function executeCrossBrandScan(): Promise<void> {
  const bootstrap = await ensureCrossBrandBootstrap(true);
  if (!bootstrap?.config.crossBrandPromptEnabled) return;

  if (isCrossBrandPromptSuppressedForDesktopSession()) {
    return;
  }

  const hids = await readAuthorizedHidDevices();
  const items = resolveCrossBrandPromptItems(bootstrap, hids);
  if (items.length > 0) {
    promptHandler?.(items);
  }
}

export function runCrossBrandScan(
  trigger: CrossBrandScanTrigger = "startup"
): Promise<void> {
  return new Promise((resolve) => {
    void (async () => {
      const bootstrap = await ensureCrossBrandBootstrap(true);
      if (!bootstrap?.config.crossBrandPromptEnabled) {
        resolve();
        return;
      }
      if (trigger === "hotplug" && !bootstrap.config.scanOnHotplugEnabled) {
        resolve();
        return;
      }

      if (trigger === "startup") {
        if (startupScanCoalesceTimer) {
          clearTimeout(startupScanCoalesceTimer);
        }
        startupScanCoalesceTimer = setTimeout(() => {
          startupScanCoalesceTimer = null;
          void executeCrossBrandScan().finally(resolve);
        }, 400);
        return;
      }

      await executeCrossBrandScan();
      resolve();
    })();
  });
}

/** 用户刚在 HID 弹窗选中的设备：若需异品牌引导则弹窗并阻止继续建链 */
export async function blockConnectIfCrossBrand(
  hids: { vendorId: number; productId: number; productName?: string }[]
): Promise<boolean> {
  const bootstrap = await ensureCrossBrandBootstrap(true);
  if (!shouldTreatHidsAsCrossBrand(hids, bootstrap)) return false;
  if (isCrossBrandPromptSuppressedForDesktopSession()) {
    return true;
  }
  const items = resolveCrossBrandPromptItems(bootstrap!, hids);
  if (items.length === 0) return false;
  promptHandler?.(items);
  return true;
}

export function scheduleCrossBrandScan(): void {
  if (scanScheduled) return;
  scanScheduled = true;
  queueMicrotask(() => {
    scanScheduled = false;
    void runCrossBrandScan("startup");
  });
}
